/* eslint @typescript-eslint/strict-boolean-expressions: off */

import React from "react";
import { ButtonOld } from "components/ButtonOld/ButtonOld";
import { ButtonGroup } from "components/ButtonOld/ButtonGroup";
import { Datepicker } from "gui/datepicker/datepicker";
import { DialogButtonPlain } from "gui/dialog/dialog";
import { FieldButtons } from "gui/input/fields";
import { FieldError } from "gui/input/fields";
import { FieldLabel } from "gui/input/fields";
import { Fields } from "gui/input/fields";
import { InputSelect } from "gui/input/input";
import { InputText } from "gui/input/input";
import { InputTextArea } from "gui/input/input";
import { Parameter } from "lib/parameters/types";
import { formatEnumOptions } from "lib/parameters/parameters";
import { logger } from "lib/logger/browser";
import { parseEnumOptions } from "lib/parameters/parameters";
import { createParameterTag } from "lib/parameters/parameters";
import styles from "gui/dialog/dialog.module.css";

interface Props {
  parameter: Parameter;
  parameters: Parameter[];
  onRemove?: (parameter: Parameter) => void;
  onReplace?: (parameter: Parameter) => void;
}

export const ParameterDialogButton: React.FC<Props> = (props) => {
  return (
    <DialogButtonPlain
      className={styles.button}
      label="Edit"
      icon="gear"
      size="sm"
    >
      {(onDismiss) => <ParameterDialog {...props} onDismiss={onDismiss} />}
    </DialogButtonPlain>
  );
};

const ParameterDialog: React.FC<
  Props & {
    onDismiss: () => void;
  }
> = (props) => {
  const [draft, setDraft] = React.useState(props.parameter);
  const [loading] = React.useState(false);
  const [error, setError] = React.useState<Error>();
  const tag = createParameterTag(draft);

  const [enumInput, setEnumInput] = React.useState(() => {
    return formatEnumOptions(props.parameter.enumOptions);
  });

  const onSubmit = (event: React.SyntheticEvent) => {
    if (!error) {
      event.preventDefault();
      props.onReplace?.(draft);
      props.onDismiss();
    }
  };

  const keyTaken = (key: string) => {
    return Boolean(
      props.parameter.key !== key && props.parameters.find((p) => p.key === key)
    );
  };

  const onKey = (event: React.ChangeEvent<HTMLInputElement>) => {
    const key = event.target.value;
    const err = !key
      ? new Error("Parameter name required.")
      : keyTaken(key)
      ? new Error("Parameter name taken.")
      : undefined;

    setDraft({ ...draft, key });
    setError(err);
  };

  const onType = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedType = event.target.value as Parameter["type"];

    if (selectedType === "enum") {
      setDraft({
        ...draft,
        type: "enum",
        value: "",
        enumOptions: [""],
      });
    } else {
      setDraft({
        ...draft,
        type: selectedType,
        value: "",
        enumOptions: undefined,
      });
    }
  };

  const onEnumOptions = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const input = event.target.value;
    const enumOptions = parseEnumOptions(input);
    const value = enumOptions[0] ?? "";
    setEnumInput(input);
    setDraft({ ...draft, enumOptions, value });
  };

  const caption =
    draft.key &&
    `Wrap your parameter in single quotes to use as a value '${tag}' in SQL.`;

  return (
    <form onSubmit={onSubmit}>
      <Fields>
        <FieldLabel label="Parameter name" caption={caption}>
          <InputText value={draft.key} onChange={onKey} required />
        </FieldLabel>
        <FieldLabel label="Parameter type">
          <InputSelect value={draft.type ?? "text"} onChange={onType}>
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="datetime">Date</option>
            <option value="enum">List</option>
          </InputSelect>
        </FieldLabel>
        <ParameterInput parameter={draft} onChange={setDraft} />
        {draft.type === "enum" && (
          <FieldLabel
            label="List values"
            caption="Separate values with newlines."
          >
            <InputTextArea value={enumInput} onChange={onEnumOptions} />
          </FieldLabel>
        )}
        <hr />
        <FieldButtons>
          <ButtonGroup>
            <ButtonOld
              loading={loading}
              disabled={Boolean(error)}
              type="submit"
              size="sm"
              color2
            >
              Save
            </ButtonOld>
            {props.onRemove && (
              <ButtonOld
                onClick={props.onRemove.bind(null, props.parameter)}
                loading={loading}
                size="sm"
                color1
                light
              >
                Remove
              </ButtonOld>
            )}
          </ButtonGroup>
          <ButtonOld
            color2
            light
            loading={loading}
            onClick={props.onDismiss}
            size="sm"
          >
            Cancel
          </ButtonOld>
        </FieldButtons>
        {error && <FieldError error={error} />}
      </Fields>
    </form>
  );
};

const ParameterInput: React.FC<{
  parameter: Parameter;
  onChange: (parameter: Parameter) => void;
}> = (props) => {
  const value = props.parameter.value ?? "";

  const onString = (value: string) => {
    props.onChange({
      ...props.parameter,
      value,
    });
  };

  const onNumber = (value: string) => {
    props.onChange({
      ...props.parameter,
      value: value.replace(/[^0-9.]/g, ""),
    });
  };

  if (props.parameter.type === "text") {
    return (
      <FieldLabel label="Default text">
        <InputText
          value={value}
          onChange={(event) => onString(event.target.value)}
          required
        />
      </FieldLabel>
    );
  }

  if (props.parameter.type === "number") {
    return (
      <FieldLabel label="Default number">
        <InputText
          value={value}
          onChange={(event) => onNumber(event.target.value)}
          required
        />
      </FieldLabel>
    );
  }

  if (props.parameter.type === "datetime") {
    return (
      <FieldLabel label="Default date and time">
        <Datepicker
          kind="dialog"
          onChange={onString}
          value={value}
          required={true}
        />
      </FieldLabel>
    );
  }

  if (props.parameter.type === "enum") {
    return (
      <FieldLabel label="Default selection">
        <InputSelect
          value={value}
          disabled={!props.parameter.enumOptions?.length}
          onChange={(event) => onString(event.target.value)}
          required
        >
          {props.parameter.enumOptions?.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </InputSelect>
      </FieldLabel>
    );
  }

  logger.warn("unsupported parameter type", props.parameter["type"]);
  return null;
};
